/**
 * Audit Logger for PHI Access
 * 
 * HIPAA-compliant logging of user actions involving PHI.
 * Also supports internal stats/analytics.
 * 
 * Usage:
 *   import { logAuditEvent } from './utils/auditLogger';
 *   
 *   // Log document view
 *   logAuditEvent('document.view', {
 *     resourceType: 'document',
 *     resourceId: docId,
 *     patientId: patientId,
 *     metadata: { documentType: 'treatment-summary' }
 *   });
 */

import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getData } from './firestoreService';

// Cache user/company info to avoid repeated Firestore reads
let cachedUserInfo = null;
let cachedUserId = null;

/**
 * Get current user info (with caching)
 */
const getUserInfo = async () => {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  // Return cached if same user
  if (cachedUserId === user.uid && cachedUserInfo) {
    return cachedUserInfo;
  }

  try {
    const userData = await getData(`users/${user.uid}`);
    cachedUserInfo = {
      userId: user.uid,
      userEmail: user.email,
      companyId: userData?.companyId || null,
      excludeFromAuditLogs: userData?.excludeFromAuditLogs || false,
    };
    cachedUserId = user.uid;
    return cachedUserInfo;
  } catch (error) {
    console.error('Error fetching user info for audit log:', error);
    // Return basic info even if Firestore read fails
    return {
      userId: user.uid,
      userEmail: user.email,
      companyId: null,
      excludeFromAuditLogs: false,
    };
  }
};

/**
 * Clear cached user info (call on logout)
 */
export const clearAuditLoggerCache = () => {
  cachedUserInfo = null;
  cachedUserId = null;
};

/**
 * Log an audit event to Firestore
 * 
 * @param {string} action - Action type (e.g., 'document.view', 'patient.create')
 * @param {Object} options - Event options
 * @param {string} [options.resourceType] - 'document' | 'patient' | 'subscription' | null
 * @param {string} [options.resourceId] - Document or patient ID
 * @param {string} [options.patientId] - Patient ID (always include for PHI)
 * @param {Object} [options.metadata] - Additional context (documentType, exportFormat, etc.)
 * @param {string} [options.userEmail] - Override user email (for auth.login.failure)
 * @returns {Promise<string|null>} - Log document ID or null on failure
 */
export const logAuditEvent = async (action, options = {}) => {
  try {
    const userInfo = await getUserInfo();
    
    // Skip logging for test users with excludeFromAuditLogs flag
    if (userInfo?.excludeFromAuditLogs) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`📋 Audit SKIPPED (test user): ${action}`);
      }
      return null;
    }
    
    // For failed logins, we may not have a user
    const userId = userInfo?.userId || options.userId || null;
    const userEmail = options.userEmail || userInfo?.userEmail || null;
    const companyId = userInfo?.companyId || options.companyId || null;

    // Don't log if we can't identify the user (except for auth failures)
    if (!userId && !action.startsWith('auth.login.failure')) {
      console.warn('Audit log skipped: no user context for action:', action);
      return null;
    }

    const logEntry = {
      // Required fields
      timestamp: serverTimestamp(),
      userId: userId,
      userEmail: userEmail,
      companyId: companyId,
      action: action,
      
      // Resource fields
      resourceType: options.resourceType || null,
      resourceId: options.resourceId || null,
      patientId: options.patientId || null,
      
      // Context
      metadata: options.metadata || {},
      
      // Client info
      ipAddress: null, // Captured at signup, not per-request
      userAgent: navigator.userAgent,
      source: 'frontend',
    };

    // Write to Firestore (async, don't await to avoid blocking UI)
    const docRef = await addDoc(collection(db, 'auditLogs'), logEntry);
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📋 Audit: ${action}`, { resourceId: options.resourceId, patientId: options.patientId });
    }
    
    return docRef.id;
  } catch (error) {
    // Log errors but don't throw - audit logging should never break the app
    console.error('Failed to write audit log:', error);
    return null;
  }
};

/**
 * Fire-and-forget version that doesn't return a promise
 * Use this when you don't need to wait for the log to complete
 */
export const logAuditEventAsync = (action, options = {}) => {
  logAuditEvent(action, options).catch(() => {
    // Errors already logged in logAuditEvent
  });
};

// Action type constants for consistency
export const AuditActions = {
  // PHI Access
  DOCUMENT_VIEW: 'document.view',
  DOCUMENT_CREATE: 'document.create',
  DOCUMENT_EDIT: 'document.edit',
  DOCUMENT_ARCHIVE: 'document.archive',
  DOCUMENT_EXPORT: 'document.export',
  PATIENT_VIEW: 'patient.view',
  PATIENT_CREATE: 'patient.create',
  PATIENT_EDIT: 'patient.edit',
  PATIENT_ARCHIVE: 'patient.archive',
  PATIENT_SHARE: 'patient.share',
  PATIENT_SHARE_UPDATE: 'patient.share.update',
  PATIENT_SHARE_REVOKE: 'patient.share.revoke',
  
  // Auth
  AUTH_LOGIN_SUCCESS: 'auth.login.success',
  AUTH_LOGIN_FAILURE: 'auth.login.failure',
  AUTH_LOGOUT: 'auth.logout',
  AUTH_SIGNUP: 'auth.signup',
  AUTH_PASSWORD_RESET: 'auth.password.reset',
  
  // Admin
  ADMIN_USER_INVITE: 'admin.user.invite',
  ADMIN_USER_REMOVE: 'admin.user.remove',
  ADMIN_SETTINGS_CHANGE: 'admin.settings.change',
  
  // Streaming
  STREAMING_START: 'streaming.start',
  STREAMING_STOP: 'streaming.stop',

  // Comments
  COMMENT_CREATE: 'comment.create',
  COMMENT_EDIT: 'comment.edit',
  COMMENT_DELETE: 'comment.delete',
  COMMENT_RESOLVE: 'comment.resolve',

  // Approval
  DOCUMENT_APPROVE: 'document.approve',
  DOCUMENT_UNAPPROVE: 'document.unapprove',
};
